/**
 * Журнал изменений номенклатуры — кто/что/когда поменял.
 *
 * Источник: GET /api/warehouse/items/audit (читает warehouse_item_audits).
 * Поддерживает фильтр по item_id (через URL ?item_id=N), но обычно
 * показывает последние 500 изменений по всем позициям.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Spinner } from "@/components/Spinner";
import { fetchItemsAudit, formatRub, type ItemAuditRow } from "@/api/warehouse";
import { ApiError } from "@/api/client";


// Перевод имени поля БД на человекочитаемое
const FIELD_LABEL: Record<string, string> = {
  sku: "Артикул",
  name: "Наименование",
  unit: "Ед. изм.",
  section: "Раздел",
  warehouse: "Склад",
  price_buy: "Цена закуп",
  price_sell: "Цена розн.",
  note: "Примечание",
};


function formatDt(iso: string): string {
  const d = new Date(iso.endsWith("Z") ? iso : iso + "Z");
  return d.toLocaleString("ru-RU", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function formatValue(v: string | null, field: string | null): string {
  if (v === null || v === "") return "—";
  if (field === "price_buy" || field === "price_sell") {
    const n = parseFloat(v);
    if (!isNaN(n)) return formatRub(n);
  }
  return v;
}


export function WarehouseItemsHistoryPage() {
  const [search, setSearch] = useState("");

  const q = useQuery({
    queryKey: ["warehouse-items-audit"],
    queryFn: () => fetchItemsAudit({ limit: 500 }),
  });

  if (q.isLoading) return <Spinner size="lg" label="Загрузка журнала изменений…" />;
  if (q.error) {
    const err = q.error;
    if (err instanceof ApiError && err.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ ограничен</div></div>;
    }
    return <div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div>;
  }

  const data = q.data!;
  const filter = search.trim().toLowerCase();
  const rows: ItemAuditRow[] = filter === ""
    ? data.items
    : data.items.filter(r =>
        (r.item_sku || "").toLowerCase().includes(filter) ||
        (r.item_name || "").toLowerCase().includes(filter) ||
        (r.user_name || "").toLowerCase().includes(filter)
      );

  return (
    <div className="page">

      <div className="filter-bar" style={{ justifyContent: "space-between", marginBottom: "var(--gap)", flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 13, color: "var(--mute)" }}>
          Последние {data.items.length.toLocaleString("ru-RU")} изменений из {data.total.toLocaleString("ru-RU")}
        </div>
        <input
          type="search" className="input-sm"
          placeholder="Поиск по артикулу / наименованию / пользователю"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ minWidth: 360 }}
        />
      </div>

      {rows.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "32px 20px" }}>
          <div className="muted">Записей нет</div>
        </div>
      ) : (
        <div className="card">
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Когда</th>
                  <th style={{ width: 160 }}>Кто</th>
                  <th style={{ width: 90 }}>Действие</th>
                  <th>Позиция</th>
                  <th style={{ width: 130 }}>Поле</th>
                  <th>Было</th>
                  <th>Стало</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const isCreate = r.action === "create";
                  return (
                    <tr key={r.id}>
                      <td className="tabular" style={{ fontSize: 12, color: "var(--mute)" }}>{formatDt(r.changed_at)}</td>
                      <td style={{ fontSize: 12 }}>{r.user_name || "—"}</td>
                      <td>
                        <span style={{
                          display: "inline-block", padding: "1px 8px", fontSize: 11,
                          borderRadius: 10, fontWeight: 600,
                          background: isCreate ? "var(--green-soft, #E6F4E1)" : "var(--paper)",
                          color: isCreate ? "var(--green, #3D7A3D)" : "var(--ink-3)",
                        }}>
                          {isCreate ? "Создано" : "Изменено"}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>
                        <div>
                          <span className="tabular" style={{ color: "var(--mute)", marginRight: 6 }}>{r.item_sku || ""}</span>
                          <span>{r.item_name || `(удалена, id=${r.item_id})`}</span>
                        </div>
                        {r.item_section && (
                          <div style={{ fontSize: 10, color: "var(--mute)" }}>{r.item_section}</div>
                        )}
                      </td>
                      <td style={{ fontSize: 12, color: "var(--ink-3)" }}>
                        {r.field ? (FIELD_LABEL[r.field] || r.field) : "—"}
                      </td>
                      <td className="tabular" style={{ fontSize: 12, color: "var(--mute)" }}>
                        {isCreate ? "—" : formatValue(r.old_value, r.field)}
                      </td>
                      <td className="tabular" style={{ fontSize: 12, fontWeight: 600 }}>
                        {formatValue(r.new_value, r.field)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
